/**
 * Main App Component
 * Routing and layout for the application
 */
import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme, CssBaseline, Box, CircularProgress, Typography } from '@mui/material';
import { AuthProvider, useAuth } from './contexts/AuthContext';

// Pages
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import HistoryPage from './pages/HistoryPage';
import ProfileCompletionPage from './pages/ProfileCompletionPage';
import SwitchAccountPage from './pages/SwitchAccountPage';

// Create Material-UI theme
const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#1976d2',
      light: '#42a5f5',
      dark: '#1565c0',
    },
    secondary: {
      main: '#9c27b0',
    },
    background: {
      default: '#f5f5f5',
      paper: '#ffffff',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h4: { fontWeight: 600 },
    h5: { fontWeight: 600 },
    h6: { fontWeight: 500 },
  },
  shape: { borderRadius: 8 },
});

const MAINTENANCE_MODE = false;
const MAINTENANCE_EXEMPT = ['madushan.jayasri@masholdings.com'];

const LoadingScreen = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
    <CircularProgress size={60} />
  </Box>
);

const MaintenanceScreen = ({ user, onLogout }) => (
  <Box sx={{
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #f5f7fa 0%, #e8edf2 100%)',
    px: 3,
    textAlign: 'center',
  }}>
    <Box sx={{
      fontSize: 72,
      mb: 3,
      lineHeight: 1,
      userSelect: 'none',
    }}>🚧</Box>
    <Typography variant="h4" sx={{ fontWeight: 700, color: '#1a1a2e', mb: 1.5 }}>
      Platform Under Development
    </Typography>
    <Typography variant="body1" sx={{ color: '#555', maxWidth: 480, mb: 1 }}>
      We're currently working on improvements to this platform.
      It will be available again once the development is complete.
    </Typography>
    <Typography variant="body2" sx={{ color: '#888', mb: 4 }}>
      Thank you for your patience.
    </Typography>
    <Box
      component="button"
      onClick={onLogout}
      sx={{
        px: 3, py: 1.2,
        border: '1px solid #ccc',
        borderRadius: 2,
        background: '#fff',
        color: '#444',
        fontSize: 14,
        cursor: 'pointer',
        '&:hover': { background: '#f0f0f0' },
      }}
    >
      Sign out ({user?.email})
    </Box>
  </Box>
);

// Requires authentication + full_name set
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, user, logout } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!isAuthenticated) return <Navigate to="/login" />;
  if (user && !user.full_name) return <Navigate to="/complete-profile" />;
  if (MAINTENANCE_MODE && !MAINTENANCE_EXEMPT.includes(user.email?.toLowerCase())) {
    return <MaintenanceScreen user={user} onLogout={logout} />;
  }
  return children;
};

// Requires authentication but full_name NOT yet set
const ProfileRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!isAuthenticated) return <Navigate to="/login" />;
  if (user?.full_name) return <Navigate to="/dashboard" />;
  return children;
};

// Public only (redirect to dashboard if authenticated + has full_name)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();
  if (loading) return <LoadingScreen />;
  if (isAuthenticated && !user?.full_name) return <Navigate to="/complete-profile" />;
  if (isAuthenticated) return <Navigate to="/dashboard" />;
  return children;
};

function AppRoutes() {
  return (
    <Router>
      <Routes>
        <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
        <Route path="/switch-account" element={<SwitchAccountPage />} />
<Route path="/complete-profile" element={<ProfileRoute><ProfileCompletionPage /></ProfileRoute>} />
        <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
        <Route path="/history" element={<ProtectedRoute><HistoryPage /></ProtectedRoute>} />
        <Route path="/" element={<Navigate to="/dashboard" />} />
      </Routes>
    </Router>
  );
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
